import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { XStack, YStack } from "tamagui";
import { z } from "zod";

import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { MaskedInput, Select } from "@/components";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import { dateOfBirthZodString } from "@/forms/ProfileForm";
import { SEX_ASSIGNED_AT_BIRTH_OPTIONS } from "@/settings/constants";

const demographicsSchema = z.object({
  dateOfBirth: dateOfBirthZodString,
  sexAssignedAtBirth: z.string().min(1, "Sex assigned at birth is required"),
});

type DemographicsForm = z.infer<typeof demographicsSchema>;

/**
 * Collects the two demographics the async exclusion criteria are judged against.
 *
 * Narrower than ConfirmDetailsStep, which also requires a name: these are asked mid-conversation,
 * where the only thing missing is what a gate needs.
 */
export default function DemographicsStep({
  onSubmitted,
  isLast,
}: {
  onSubmitted: () => void;
  /** Whether this is the panel's final page. Drives the submit button's accessible name. */
  isLast: boolean;
}) {
  const { data: profile } = useGetUserProfile();
  const { mutate: updateUserProfile, isPending } = useUpdateUserProfile();

  const { control, handleSubmit } = useForm<DemographicsForm>({
    resolver: zodResolver(demographicsSchema),
    mode: "onBlur",
    values: {
      dateOfBirth: profile?.dateOfBirth ?? "",
      sexAssignedAtBirth: profile?.sexAssignedAtBirth ?? "",
    },
  });

  const onSubmit = handleSubmit((formData) => {
    updateUserProfile({ data: formData }, { onSuccess: onSubmitted });
  });

  return (
    <>
      <YStack paddingHorizontal="$150" gap="$150">
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <MaskedInput.Controlled
              label="Date of birth"
              mask={Masks.DATE_MMDDYYYY}
              placeholder="mm/dd/yyyy"
              inputMode="numeric"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="sexAssignedAtBirth"
          render={({ field, fieldState }) => (
            <Select
              ref={field.ref}
              name={field.name}
              label="Sex at birth"
              items={SEX_ASSIGNED_AT_BIRTH_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Select"
              errorText={fieldState.error?.message}
            />
          )}
        />
      </YStack>
      <XStack
        justifyContent="flex-end"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={isPending}
          isLast={isLast}
          onSkip={onSubmit}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
